import React from "react";
import io, { Socket } from "socket.io-client";
import { useSelector } from "react-redux";
import type { StateNetwork } from "src/store/index.reducer";
import type { UserState } from "src/store/User/user.reducer";
import { getBaseURL } from "src/helpers";

interface UseSocket {
	notifs: any[];
	changeAccessToken: (value: string) => void;
	socket: Socket | null;
	socketConnected: boolean;
}

const useSocket = (defaultToken?: string): UseSocket => {
	const [notifs, setNotifs] = React.useState<any[]>([]);
	const [socket, setSocket] = React.useState<Socket | null>(null);
	const [socketConnected, setSocketConnected] = React.useState(false);
	const [accessToken, setAccessToken] = React.useState(defaultToken);

	const { userInfo } = useSelector<StateNetwork, UserState>((state) => state.user);
	const userId = userInfo?._id;

	// Connect socket when accessToken changes
	React.useEffect(() => {
		if (!accessToken) return;

		const newSocket = io(`${getBaseURL()}?token=${accessToken}`, {
			path: "/notifications/socket",
			reconnectionAttempts: 20,
			timeout: 10000,
			reconnectionDelay: 5000,
			forceNew: true,
		});

		setSocket(newSocket);

		return () => {
			newSocket.disconnect();
			setSocket(null);
			setSocketConnected(false);
		};
	}, [accessToken]);

	React.useEffect(() => {
		if (!socket) return;

		const onNewNotification = (data: any) => {
			setNotifs((prev) => [...prev, data.payload.notification]);
		};

		const onError = (error: any) => {
			console.log("websocket error>", error);
			socket.close();
		};

		const onDisconnect = () => {
			console.log("websocket disconnected>");
			setSocketConnected(false);
		};

		const onConnectTimeout = () => {
			console.log("websocket connect_timeout>");
			socket.close();
		};

		const onReconnectAttempt = (attemptNumber: number) => {
			console.log("websocket reconnect_attempt>", attemptNumber);
		};

		const onConnect = () => {
			console.log("websocket connected>");
			setSocketConnected(true);
		};

		const onConnectError = (error: any) => {
			console.log("websocket connect_error>", error);
			socket.close();
			setSocketConnected(false);
		};

		const onReconnectError = (error: any) => {
			console.log("websocket reconnect_error>", error);
		};

		socket.on("new-notification-event", onNewNotification);
		socket.on("error", onError);
		socket.on("disconnect", onDisconnect);
		socket.on("connect_timeout", onConnectTimeout);
		socket.on("reconnect_attempt", onReconnectAttempt);
		socket.on("connect", onConnect);
		socket.on("connect_error", onConnectError);
		socket.on("reconnect_error", onReconnectError);

		return () => {
			socket.off("new-notification-event", onNewNotification);
			socket.off("error", onError);
			socket.off("disconnect", onDisconnect);
			socket.off("connect_timeout", onConnectTimeout);
			socket.off("connect", onConnect);
			socket.off("connect_error", onConnectError);
			socket.off("reconnect_error", onReconnectError);
		};
	}, [socket]);

	// Authenticate socket
	React.useEffect(() => {
		if (socketConnected && socket && userId) {
			socket.emit("authenticate", { userId });
		}
	}, [socketConnected, socket, userId]);

	function changeAccessToken(token: string) {
		setAccessToken(token);
	}

	return {
		changeAccessToken,
		notifs,
		socket,
		socketConnected,
	};
};

export default useSocket;
